import styled from '@emotion/styled';
import { useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useConsumerT } from '../translations/landingConsumer';
import ImpactMetric from '../components/ImpactMetric';
import LotteryForm from '../components/LotteryForm';
import { useConsumer } from '../../contexts/ConsumerContext';
import { useWellDoneContext } from '../pages/WellDone/WellDonePageContext';
import AlertSnackbar from '../components/AlertSnackbar';
import { useParcelLoader } from '../hooks/useParcelLoader';
import { postLotteryParticipation } from '../../services';

/**
 * BravoScreenContainer — écran 5 (impact + jeu concours).
 */

export default function WellDoneScreenContainer() {
  const t = useConsumerT();
  const theme = useTheme();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { dispatch: consumerDispatch } = useConsumer();
  const rescanActionType = locationState?.flow === 'sendback' ? 'returnOrder' : 'returnPackage';
  const { parcel, brand, loading } = useParcelLoader();
  const { fetchAlert, alertMessage, dispatch: wellDonePageDispatch } = useWellDoneContext();

  function handleAlertClose() {
    wellDonePageDispatch({ type: 'PAGE_WELL_DONE_STATES_UPDATE', payload: { fetchAlert: false, alertMessage: '' } });
  }

  const brandData = brand || {};
  const impact = brandData?.impact || { co2Saved: '87%', waterSaved: '64%' };

  async function handleLotterySubmit({ email, phone, marketingAccepted }) {
    if (!parcel?.id) {
      wellDonePageDispatch({
        type: 'PAGE_WELL_DONE_STATES_UPDATE',
        payload: { fetchAlert: true, alertMessage: t('bravo.lottery-error-missing-parcel') },
      });
      return false;
    }

    if (!parcel?.shippingNumber) {
      wellDonePageDispatch({
        type: 'PAGE_WELL_DONE_STATES_UPDATE',
        payload: { fetchAlert: true, alertMessage: t('bravo.lottery-error-missing-shipping') },
      });
      return false;
    }

    try {
      const result = await postLotteryParticipation({
        parcelID: parcel.id,
        shippingNumber: parcel.shippingNumber,
        phoneNumber: phone || '',
        email,
        marketingConsent: marketingAccepted || false,
        marketingConsentDate: marketingAccepted ? new Date().toISOString() : undefined,
      });
      if (result.status < 200 || result.status >= 300) {
        const error = new Error(`Code: ${result.status}`);
        error.status = result.status;
        throw error;
      }
      return true;
    } catch (error) {
      console.error('handleLotterySubmit: failed', error);
      if (error.status === 409) return 'duplicate';
      let errorMessage = t('bravo.lottery-error-generic');
      if (error.status === 400) errorMessage = t('bravo.lottery-error-invalid');
      else if (error.status === 404) errorMessage = t('bravo.lottery-error-not-found');
      wellDonePageDispatch({
        type: 'PAGE_WELL_DONE_STATES_UPDATE',
        payload: { fetchAlert: true, alertMessage: errorMessage },
      });
      return false;
    }
  }

  const heroBg = brandData?.brandGradient || `linear-gradient(150deg, ${theme.palette.accent.main} 0%, #1B2E28 100%)`;
  const heroTextColor = brandData?.brandTextColor || '#FFFFFF';

  if (loading) {
    return (
      <GuardPage>
        <CircularProgress size={32} sx={{ color: theme.palette.accent.main }} />
      </GuardPage>
    );
  }

  if (!parcel) {
    return (
      <GuardPage>
        <GuardCard>
          <GuardEmoji>📦</GuardEmoji>
          <GuardMessage>{t('bravo.no-parcel-message')}</GuardMessage>
          <GuardSecondaryBtn onClick={() => {
            consumerDispatch({ type: 'CONSUMER_RESET_KEEP_BRAND' });
            navigate('/');
          }}>
            {t('bravo.restart-cta')}
          </GuardSecondaryBtn>
        </GuardCard>
      </GuardPage>
    );
  }

  return (
    <Page>
      <AlertSnackbar open={fetchAlert} message={alertMessage} onClose={handleAlertClose} />
      <Hero style={{ background: heroBg }} $textColor={heroTextColor}>
        <HeroInner>
          <HeroLine1>{t('bravo.heading-line-1')}</HeroLine1>
          <HeroLine2>{t('bravo.heading-line-2')}</HeroLine2>
          <HeroBody>{t('bravo.subheading')}</HeroBody>
        </HeroInner>
      </Hero>

      <Content>
        <ImpactCard>
          <ImpactLabel>{t('bravo.impact-label')}</ImpactLabel>
          <ImpactRow>
            <ImpactMetric emoji="♻️" value={t('bravo.impact-parcel-value')} label={t('bravo.impact-parcel-label')} />
            <VerticalDivider />
            <ImpactMetric emoji="🌍" value={t('bravo.impact-co2-value', { co2Saved: impact.co2Saved })} label={t('bravo.impact-co2-label')} />
            <VerticalDivider />
            <ImpactMetric emoji="💧" value={t('bravo.impact-water-value', { waterSaved: impact.waterSaved })} label={t('bravo.impact-water-label')} />
          </ImpactRow>
        </ImpactCard>

        <LotteryCard>
          <LotteryHeader>{t('bravo.lottery-header')}</LotteryHeader>
          <LotteryBody>
            <LotteryIntro>
              <p>{t('bravo.lottery-intro-1')}</p>
              <p>{t('bravo.lottery-intro-2')}</p>
            </LotteryIntro>
            <WarmBox>
              <WarmLabel>{t('bravo.lottery-prize-label')}</WarmLabel>
              <WarmValue>{t('bravo.lottery-prize-value')}</WarmValue>
            </WarmBox>
            <LotteryForm
              onSubmit={handleLotterySubmit}
              onSuccessClose={() => {
                consumerDispatch({ type: 'CONSUMER_RESET_KEEP_BRAND' });
                navigate('/');
              }}
              onDuplicateRescan={() => {
                consumerDispatch({ type: 'CONSUMER_RESET_KEEP_BRAND' });
                navigate(`/scan?actionType=${rescanActionType}`);
              }}
            />
          </LotteryBody>
        </LotteryCard>

        <RestartBtn type="button" onClick={() => {
          consumerDispatch({ type: 'CONSUMER_RESET_KEEP_BRAND' });
          navigate('/');
        }}>
          {t('bravo.restart-cta')}
        </RestartBtn>
      </Content>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.palette.background.default};
  font-family: ${p => p.theme.typography.fontFamily};
`;

const Hero = styled.div`
  padding: 56px 20px 72px;
  text-align: center;
  color: ${p => p.$textColor || '#FFFFFF'};
`;

const HeroInner = styled.div`
  max-width: 480px;
  margin: 0 auto;
`;

const HeroLine1 = styled.div`
  font-size: 38px;
  font-weight: 900;
  letter-spacing: -1px;
  line-height: 1;
`;

const HeroLine2 = styled.div`
  font-size: 58px;
  font-weight: 900;
  letter-spacing: -2px;
  line-height: 1;
  margin-bottom: 16px;
`;

const HeroBody = styled.p`
  font-size: 14px;
  line-height: 1.55;
  margin: 0;
  opacity: 0.9;
`;

const Content = styled.div`
  background: ${p => p.theme.palette.background.default};
  margin-top: -32px;
  border-radius: 24px 24px 0 0;
  padding: 20px 20px 32px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  z-index: 1;
`;

const ImpactCard = styled.div`
  background: #F5F5F5;
  border-radius: 16px;
  padding: 18px 16px;
  border: 1px solid ${p => p.theme.palette.border.main};
`;

const ImpactLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: ${p => p.theme.palette.text.disabled};
  text-align: center;
  margin-bottom: 12px;
`;

const ImpactRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 4px;
`;

const VerticalDivider = styled.div`
  width: 1px;
  background: ${p => p.theme.palette.border.main};
  margin: 4px 0;
`;

// Palette inspirée du ticket doré de Charlie et la Chocolaterie (version soft)
const GOLD_PRIMARY = '#D9A641';    // Or doux principal
const GOLD_LIGHT = '#F4E5C3';      // Crème doré
const GOLD_DARK = '#8B6914';       // Or foncé pour le texte
const CREAM = '#FFF9E6';           // Ivoire crème

const LotteryCard = styled.div`
  background: linear-gradient(135deg, ${CREAM} 0%, #FDEFC9 50%, ${GOLD_LIGHT} 100%);
  border: 2px solid ${GOLD_PRIMARY};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 
    0 4px 24px rgba(217, 166, 65, 0.2),
    0 0 60px rgba(217, 166, 65, 0.15),
    inset 0 0 40px rgba(255, 255, 255, 0.3);
  position: relative;
  
  /* Effet brillance dorée */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4) 50%,
      transparent
    );
    animation: shine 3s infinite;
  }
  
  @keyframes shine {
    0% { left: -100%; }
    50%, 100% { left: 200%; }
  }
`;

const LotteryHeader = styled.div`
  background: linear-gradient(135deg, ${GOLD_PRIMARY} 0%, #C79434 100%);
  border-bottom: 2px solid ${GOLD_DARK};
  color: #FFFFFF;
  padding: 12px 20px;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-align: center;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 1;
`;

const LotteryBody = styled.div`
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const LotteryIntro = styled.div`
  text-align: center;
  p {
    font-size: 13px;
    color: ${GOLD_DARK};
    line-height: 1.55;
    margin: 0 0 4px;
    font-weight: 500;
  }
  p:last-child {
    margin-bottom: 0;
  }
`;

const WarmBox = styled.div`
  border: 2.5px solid ${GOLD_PRIMARY};
  background: linear-gradient(135deg, #FFFFFF 0%, ${CREAM} 100%);
  border-radius: 14px;
  padding: 16px 18px;
  text-align: center;
  box-shadow: 
    0 4px 16px rgba(217, 166, 65, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  position: relative;
  
  /* Coins décorés style ticket premium */
  &::before,
  &::after {
    content: '✦';
    position: absolute;
    font-size: 16px;
    color: ${GOLD_PRIMARY};
    opacity: 0.6;
  }
  
  &::before {
    top: 8px;
    left: 12px;
  }
  
  &::after {
    bottom: 8px;
    right: 12px;
  }
`;

const WarmLabel = styled.div`
  font-size: 11px;
  font-weight: 900;
  color: ${GOLD_PRIMARY};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 6px;
  text-shadow: 0 1px 2px rgba(217, 166, 65, 0.3);
`;

const WarmValue = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: ${GOLD_DARK};
  line-height: 1.3;
`;

const GuardPage = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.palette.background.default};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  font-family: ${p => p.theme.typography.fontFamily};
`;

const GuardCard = styled.div`
  background: ${p => p.theme.palette.background.paper};
  border: 1px solid ${p => p.theme.palette.border.main};
  border-radius: 16px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 360px;
  width: 100%;
  box-shadow: ${p => p.theme.shadows[2]};
  text-align: center;
`;

const GuardEmoji = styled.div`
  font-size: 48px;
  line-height: 1;
`;

const GuardMessage = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.theme.palette.text.secondary};
  line-height: 1.6;
  margin: 0;
`;

const GuardSecondaryBtn = styled.button`
  width: 100%;
  background: ${p => p.theme.palette.accent.main};
  color: #FFFFFF;
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 700;
  font-family: ${p => p.theme.typography.fontFamily};
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover { opacity: 0.9; }
  &:active { opacity: 0.8; }
`;

const RestartBtn = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 999px;
  background: transparent;
  border: 1px solid ${p => p.theme.palette.border.main};
  color: ${p => p.theme.palette.text.disabled};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: ${p => p.theme.typography.fontFamily};
  margin-bottom: 16px;
`;
